import classNames from "classnames";
import { JSX, PropsWithChildren } from "react";
import { Link, LinkProps } from "react-router";
import { Glyph } from "../types";
import styles from "./Button.module.css";
import Icon from "./Icon";
import West from "./icons/west.svg?react";

type ButtonType =
  | "accent"
  | "accent_secondary"
  | "primary"
  | "secondary"
  | "error";

type ButtonStyle = "fill" | "underline";

type ButtonSize = "default" | "small" | "large";

type ButtonProps = PropsWithChildren<{
  type?: ButtonType;
  style?: ButtonStyle;
  size?: ButtonSize;
  isLoading?: boolean;
  fullWidth?: boolean;
  buttonProps?: JSX.IntrinsicElements["button"];
}>;

const createClassNames = ({
  type = "primary",
  style = "fill",
  size = "default",
  fullWidth,
  buttonProps,
}: ButtonProps) =>
  classNames(
    buttonProps?.className,
    styles.btn,
    styles.primary,
    styles.default,
    {
      [styles.disabled]: buttonProps?.disabled,
      [styles.secondary]: type === "secondary",
      [styles.accent]: type === "accent",
      [styles.accentSecondary]: type === "accent_secondary",
      [styles.error]: type === "error",
      [styles.underline]: style === "underline",
      [styles.small]: size === "small",
      [styles.fullWidth]: fullWidth,
    },
  );

const Button = (props: ButtonProps) => {
  const { children, isLoading, buttonProps } = props;
  return (
    <button type="button" {...buttonProps} className={createClassNames(props)}>
      {children}
      {isLoading && " ..."}
    </button>
  );
};

interface IconButtonProps extends ButtonProps {
  glyph: Glyph;
  className?: string;
  rounded?: boolean;
}

export const IconButton = ({
  glyph,
  className,
  rounded,
  size,
  buttonProps,
}: IconButtonProps) => (
  <button
    role="checkbox"
    type="button"
    aria-checked="true"
    className={classNames(styles.btn, styles.iconBtn, {
      [styles.disabled]: buttonProps?.disabled,
      [styles.rounded]: rounded,
      [styles.large]: size === "large",
    })}
    {...buttonProps}
  >
    <Icon className={classNames(className)} glyph={glyph} />
  </button>
);

interface LinkButtonProps extends IconButtonProps {
  linkProps: LinkProps;
}

export const LinkButton = (props: LinkButtonProps) => {
  const { children, glyph, linkProps, className } = props;
  return (
    <Link {...linkProps} className={classNames(className, styles.linkBtn)}>
      {children}
      <Icon glyph={glyph} className={styles.icon} />
    </Link>
  );
};

export const BackLink = (props: LinkProps) => {
  const { children } = props;
  return (
    <LinkButton glyph={West} linkProps={props}>
      {children}
    </LinkButton>
  );
};

export default Button;
